import { useEffect, useState } from "react";
import { useSession } from "Context/userAuthContext";
import { logOut, publishProfile, uploadImage } from "Util/firebaseHelper";
import { Formik, Form } from "formik";
import { profileValidationSchema } from "Model/Profile";
import { editSellerProfile } from "Redux/actionCreators";
import { useAppDispatch } from "Redux/hooks";
import USStates from "Components/USStates.json";

import {
  Button,
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Typography,
  Stack,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  Checkbox,
  Divider,
} from "@mui/material";
import { InfoBox } from "Components/AllComponents";
import LoadingButton from "@mui/lab/LoadingButton";
import Avatar from "boring-avatars";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  getStateAbbreviation,
  getStateName,
  resizeFile,
  compressImage,
} from "Util/helpers";

type Props = {
  autoPop: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
};
export const ProfileSettings = ({
  autoPop,
  isModalOpen,
  openModal,
  closeModal,
}: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { user, sellerProfile } = useSession();
  const dispatch = useAppDispatch();

  const [uploadingImage, setUploadingImage] = useState(false);
  useEffect(() => {
    if (autoPop) {
      openModal();
    }
  }, [autoPop]);

  const handleCloseProfile = async () => {
    //props.resetForm();
    if (autoPop) {
      await logOut();
      window.location.reload();
    }
    closeModal();
  };

  return (
    <div>
      <Dialog
        fullWidth
        fullScreen={fullScreen}
        open={isModalOpen}
        onClose={() => {
          handleCloseProfile();
        }}
      >
        <DialogTitle>
          Settings
          {user ? null : (
            <InfoBox sx={{ mt: 1 }}>
              <Typography variant="body1">
                Please sign in to save changes.
              </Typography>
            </InfoBox>
          )}
        </DialogTitle>
        <Formik
          validateOnChange={false}
          initialValues={sellerProfile}
          enableReinitialize={true}
          validationSchema={profileValidationSchema(
            sellerProfile.username.toLowerCase()
          )}
          onSubmit={(values, actions) => {
            //alert(JSON.stringify(values, null, 2));
            publishProfile(
              {
                ...values,
                username: values.username.toLowerCase(),
                links: sellerProfile.links,
                sellerEmail: user?.email || "",
              },
              user?.uid || ""
            );
            actions.resetForm({
              values: values,
            });
            dispatch(editSellerProfile(values));
            closeModal();
          }}
        >
          {(props) => (
            <Form>
              <DialogContent dividers>
                <Stack spacing={1}>
                  <Typography variant="h6">Username</Typography>
                  <TextField
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography color="text.primary">
                            flowlylink.com/
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    required
                    id="username"
                    name="username"
                    value={props.values.username.toLowerCase()}
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    error={
                      props.touched.username && Boolean(props.errors.username)
                    }
                    helperText={props.touched.username && props.errors.username}
                  />
                </Stack>

                <Typography variant="h6" mt={4} mb={1}>
                  Business info
                </Typography>
                <Box
                  sx={{
                    aspectRatio: "1/1",
                    borderRadius: "50%",
                    backgroundColor: "background.default",
                    width: "64px",
                    overflow: "hidden",
                  }}
                >
                  {props.values.sellerPhoto === "" ? (
                    <LoadingButton
                      color="secondary"
                      component="label"
                      loading={uploadingImage}
                      sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "1/1",
                        backgroundColor: "background.default",
                        overflow: "hidden",
                        "&:hover": { opacity: 0.8 },
                        textAlign: "center",
                      }}
                    >
                      Logo
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={async (e) => {
                          // e.preventDefault();
                          setUploadingImage(true);
                          if (
                            user &&
                            e.target.files &&
                            e.target.files.length > 0
                          ) {
                            const image = e.target.files[0];
                            const resizedImage = await resizeFile(
                              image,
                              300,
                              300
                            );
                            var url = await uploadImage(
                              resizedImage as File,
                              user.uid
                            );
                            props.setFieldValue("sellerPhoto", url);
                            console.log("uploaded photo", url);
                          }
                          setUploadingImage(false);
                        }}
                      />
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      color="secondary"
                      component="label"
                      loading={uploadingImage}
                      sx={{
                        position: "relative",
                        width: "100%",
                        p: 0,
                      }}
                    >
                      <img
                        src={props.values.sellerPhoto}
                        width={64}
                        height={64}
                        style={{ objectFit: "cover", borderRadius: "50%" }}
                      />
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={async (e) => {
                          // e.preventDefault();
                          setUploadingImage(true);
                          if (
                            user &&
                            e.target.files &&
                            e.target.files.length > 0
                          ) {
                            const image = e.target.files[0];
                            const resizedImage = await compressImage(
                              image,
                              300,
                              300
                            );
                            var url = await uploadImage(
                              resizedImage as File,
                              user.uid
                            );
                            props.setFieldValue("sellerPhoto", url);
                            console.log(
                              "uploaded photo",
                              url,
                              "Size:",
                              resizedImage.size
                            );
                          }
                          setUploadingImage(false);
                        }}
                      />
                    </LoadingButton>
                  )}
                </Box>
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  id="businessName"
                  name="businessName"
                  label="Business name"
                  value={props.values.businessName}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  error={
                    props.touched.businessName &&
                    Boolean(props.errors.businessName)
                  }
                  helperText={
                    props.touched.businessName && props.errors.businessName
                  }
                  sx={{ mt: 3 }}
                />
                <Grid container columnSpacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="outsideUS"
                          checked={props.values.outsideUS}
                          onChange={(e) => {
                            props.setFieldValue("outsideUS", e.target.checked);
                            // Clear state when toggling in either direction
                            props.setFieldValue("sellerState", "");
                          }}
                        />
                      }
                      label="I'm outside US"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      required
                      id="sellerCity"
                      name="sellerCity"
                      label="City"
                      value={props.values.sellerCity}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      error={
                        props.touched.sellerCity &&
                        Boolean(props.errors.sellerCity)
                      }
                      helperText={
                        props.touched.sellerCity && props.errors.sellerCity
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <FormControl required fullWidth sx={{ mt: 2, mb: 1 }}>
                      {props.values.outsideUS ? (
                        <TextField
                          fullWidth
                          required
                          id="sellerState"
                          name="sellerState"
                          label="State"
                          value={props.values.sellerState}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          error={
                            props.touched.sellerState &&
                            Boolean(props.errors.sellerState)
                          }
                          helperText={
                            props.touched.sellerState &&
                            props.errors.sellerState
                          }
                        />
                      ) : (
                        <Stack>
                          <InputLabel>State</InputLabel>
                          <Select
                            label="State"
                            value={props.values.sellerState}
                            onChange={(e) => {
                              props.setFieldValue(
                                "sellerState",
                                e.target.value
                              );
                            }}
                          >
                            {USStates.map((state) => (
                              <MenuItem
                                value={state.abbreviation}
                                key={state.name}
                              >
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Stack>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      required
                      id="sellerZip"
                      name="sellerZip"
                      label="Zip code"
                      value={props.values.sellerZip}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      error={
                        props.touched.sellerZip &&
                        Boolean(props.errors.sellerZip)
                      }
                      helperText={
                        props.touched.sellerZip && props.errors.sellerZip
                      }
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" mt={3}>
                  About you
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  id="sellerName"
                  name="sellerName"
                  label="Name"
                  value={props.values.sellerName}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                />
                <TextField
                  fullWidth
                  multiline
                  margin="normal"
                  id="sellerIntro"
                  name="sellerIntro"
                  label="Bio"
                  value={props.values.sellerIntro}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                />

                <Typography variant="h6" mt={4} mb={2}>
                  Custom order
                </Typography>
                <FormControl required fullWidth margin="none">
                  <FormLabel>Fulfillment</FormLabel>
                  <RadioGroup
                    name="fulfillmentMethod"
                    id="fulfillmentMethod"
                    value={props.values.fulfillmentMethod}
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                  >
                    <Stack direction="row" spacing={1}>
                      <FormControlLabel
                        value="Pickup only"
                        control={<Radio />}
                        label="Pickup only"
                      />
                      <FormControlLabel
                        value="Pickup or shipping"
                        control={<Radio />}
                        label="Pickup or shipping"
                      />
                    </Stack>
                  </RadioGroup>
                </FormControl>
                {props.values.fulfillmentMethod === "Pickup or shipping" ? (
                  <FormControlLabel
                    control={<Checkbox />}
                    name="shipOnlyToMyState"
                    label="Limit shipping to my state"
                    onChange={props.handleChange}
                    checked={props.values.shipOnlyToMyState}
                    sx={{ paddingLeft: 1.25 }}
                  />
                ) : null}

                <Typography variant="h6" mt={3} mb={1}>
                  Presale
                </Typography>
                <Grid container columnSpacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      margin="dense"
                      id="defaultPickupAddress"
                      name="defaultPickupAddress"
                      label="Default pickup address"
                      value={props.values.defaultPickupAddress}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Saved to streamline presale pickup setup. It will never be
                      shared without your permission.
                    </Typography>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                {autoPop ? null : (
                  <Button
                    onClick={handleCloseProfile}
                    disabled={!user}
                    color="secondary"
                  >
                    Cancel
                  </Button>
                )}
                <Button type="submit" variant="contained" disabled={!user}>
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};
